import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Spinner,
} from "@chakra-ui/react";
import { toast } from "react-toastify";
import PaymentApiNovo from "../../../../services/paymentApiNovo";
import Login from "../../../../services/loginApi";

interface ModalConfirmPaymentCodeProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  paymentId: number;
  valor: number;
  email: string;
  onConfirm: () => Promise<void>;
}

const ModalConfirmPaymentCode = ({
  isOpen,
  setIsOpen,
  paymentId,
  valor,
  email,
  onConfirm,
}: ModalConfirmPaymentCodeProps) => {
  const [codes, setCodes] = useState<string[]>(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const handleClose = () => {
    setIsOpen(false);
    setCodes(["", "", "", "", "", ""]);
  };

  const requestVerificationCode = async () => {
    if (!email) {
      console.error("Email is empty or undefined:", email);
      toast.error("Email do usuário não encontrado. Faça login novamente.");
      handleClose();
      return;
    }

    setLoading(true);
    try {
      console.log("Requesting verification code with:", {
        type: "pagamento",
        email,
      });
      await Login.GetNewCodeEmail("pagamento", email);
      toast.success("Código de segurança enviado para seu e-mail ou SMS!");
    } catch (error: any) {
      console.error(
        "Failed to request verification code:",
        error.response || error.message
      );
      toast.error(
        `Falha ao solicitar código de segurança: ${
          error.message || "Erro desconhecido"
        }`
      );
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    const newCodes = [...codes];
    newCodes[index] = value.replace(/\D/g, "").slice(0, 1);
    setCodes(newCodes);
    if (value && index < codes.length - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Backspace" && !codes[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = event.clipboardData.getData("text").slice(0, 6);
    const newCodes = paste.padEnd(6, "").split("").slice(0, 6);
    setCodes(newCodes);

    newCodes.forEach((value, index) => {
      if (inputsRef.current[index]) {
        inputsRef.current[index].value = value;
      }
    });

    const nextIndex = paste.length < 6 ? paste.length : 5;
    if (inputsRef.current[nextIndex]) {
      inputsRef.current[nextIndex].focus();
    }
  };

  const handleSubmit = async () => {
    const code = codes.join("");
    if (code.length !== 6) {
      toast.error("Por favor, insira um código de 6 dígitos.");
      return;
    }

    setLoading(true);
    try {
      console.log("Confirming payment with:", { paymentId, code, valor });
      await PaymentApiNovo.confirmPayment(paymentId, code, valor);
      toast.success("Pagamento confirmado com sucesso!");
      await onConfirm();
      handleClose();
    } catch (error: any) {
      console.error(
        "Failed to confirm payment:",
        error.response || error.message
      );
      toast.error(
        `Falha ao confirmar pagamento: ${
          error?.response?.data || error.message
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      console.log("Modal opened, requesting verification code...");
      setCodes(["", "", "", "", "", ""]);
      requestVerificationCode();
    }
  }, [isOpen, email]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader>
          <Flex w="100%" justifyContent="center" my={2}>
            <Text fontSize="xl" fontWeight="bold">
              Digite o código de verificação
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <Flex justifyContent="center" alignItems="center" height="100px">
              <Spinner size="lg" />
            </Flex>
          ) : (
            <Flex w="100%" justifyContent="center">
              {codes.map((value, index) => (
                <Input
                  key={index}
                  ref={(el) =>
                    (inputsRef.current[index] = el as HTMLInputElement)
                  }
                  type="text"
                  maxLength={1}
                  width="3rem"
                  textAlign="center"
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  marginRight={2}
                />
              ))}
            </Flex>
          )}
          <Text mt={4} textAlign="center" fontSize="sm" color="gray.500">
            Um código de 6 dígitos foi enviado para {email}.
          </Text>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <Button
            onClick={handleSubmit}
            colorScheme="blue"
            isDisabled={codes.some((code) => !code) || loading}
            mr={3}
          >
            Confirmar
          </Button>
          <Button onClick={handleClose} colorScheme="gray" isDisabled={loading}>
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalConfirmPaymentCode;
